// The news pages (/news and /news/:slug) show webadmin's published `news`
// collection and nothing else, each post exactly as published: its own slug
// (the address), title, summary, text, category, department, author, cover
// photo and external link. The prototype stories that used to live in
// lib/news.ts are gone; until webadmin publishes a post, the list says so.
//
// Date: a post's only date is `publishedAt`, shown as the day it falls on in
// India.
//
// Order: webadmin's `order` first (a coarse priority; its imports default to
// 999), then the newest.
//
// Text: `content` is plain text. The post's page lays it out the way event
// descriptions are (eventDescription.ts): a paragraph per blank-line-separated
// chunk, a list per run of "- " lines. A card shows `summary`, or the text
// itself when no summary is published, clamped by CSS rather than cut here.
//
// Photos: process-content-images.mjs reprocesses `cover` for this collection
// (sites/biet/site-content/news/<slug>.<ext>) the way it does events' banner.
// asMedia takes only those bare keys, so a cover that hasn't been reprocessed
// shows no photo rather than bypassing the transform CDN. `images[]` and
// attachments aren't shown, for the reason events don't show theirs: neither
// says what any one image or file is.
import type { Media } from "@zivin-labs/site-content";
import { useCollection } from "./siteContent";
import { resolveDepartment } from "./departments";

interface PublishedMediaRef {
  url?: string;
  alt?: string;
  width?: number;
  height?: number;
}

interface PublishedNewsRecord {
  slug: string;
  title: string;
  summary?: string | null;
  content?: string | null;
  category?: string | null;
  department?: { code: string; name: string; slug: string } | null;
  authorName?: string | null;
  externalUrl?: string | null;
  featured?: boolean;
  order?: number;
  cover?: PublishedMediaRef | null;
  publishedAt: string;
}

export interface PublishedNewsPost {
  slug: string;
  title: string;
  summary: string;
  content: string;
  category: string;
  departmentName: string;
  /** The department's page on this site, when the published department is one of ours. */
  departmentSlug?: string;
  authorName: string;
  externalUrl?: string;
  featured: boolean;
  /** As published (ISO); `date` is its IST display form. */
  publishedAt: string;
  date: { day: string; month: string; year: string };
  cover?: Media;
}

/** Same bare-key discipline as publishedEvents.ts's asMedia — never route an unprocessed webadmin URL through the transform CDN. */
function asMedia(ref: PublishedMediaRef | null | undefined, fallbackAlt: string): Media | undefined {
  if (!ref?.url || ref.url.includes("://") || !ref.width || !ref.height) return undefined;
  return { url: ref.url, alt: ref.alt || fallbackAlt, width: ref.width, height: ref.height };
}

const IST_DATE = new Intl.DateTimeFormat("en-US", {
  timeZone: "Asia/Kolkata",
  day: "numeric",
  month: "short",
  year: "numeric",
});

function dateOf(iso: string): PublishedNewsPost["date"] {
  const parts = IST_DATE.formatToParts(new Date(iso));
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return { day: part("day"), month: part("month"), year: part("year") };
}

export function usePublishedNews(): { posts: PublishedNewsPost[]; loading: boolean } {
  const { items, loading } = useCollection("news");
  const records = [...(items as unknown as PublishedNewsRecord[])]
    .filter((r) => r.slug && r.title?.trim())
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || Date.parse(b.publishedAt) - Date.parse(a.publishedAt));

  const posts = records.map((r): PublishedNewsPost => {
    const title = r.title.trim();
    return {
      slug: r.slug,
      title,
      summary: r.summary?.trim() ?? "",
      content: r.content?.trim() ?? "",
      category: r.category?.trim() ?? "",
      departmentName: r.department?.name ?? "",
      departmentSlug: r.department ? resolveDepartment(r.department.code)?.slug : undefined,
      authorName: r.authorName?.trim() ?? "",
      externalUrl: r.externalUrl?.trim() || undefined,
      featured: Boolean(r.featured),
      publishedAt: r.publishedAt,
      date: dateOf(r.publishedAt),
      cover: asMedia(r.cover, title),
    };
  });
  return { posts, loading };
}

/** A department's own published posts, in the news page's order — matched on the published department resolving to this department's page. */
export function useDepartmentNews(departmentSlug: string | undefined): PublishedNewsPost[] {
  const { posts } = usePublishedNews();
  if (!departmentSlug) return [];
  return posts.filter((n) => n.departmentSlug === departmentSlug);
}
